import { type FC } from 'react';

import { useNextSpecContractTime } from '@/common/hooks/next-spec-contract-time';
import {
  type PaidSpecContractType,
  type SpecContractStatusType,
  type SpecContractType,
} from '@/common/types/contract';

import { AdditionalTimeInfo } from '../../AdditionalTimeInfo/AdditionalTimeInfo';
import { ContractButtons } from '../../ContractButtons/ContractButtons';
import { ContractDescription } from '../../ContractDescription/ContractDescription';
import { ContractExpiredText } from '../../ContractExpiredText/ContractExpiredText';
import { ContractRewardsList } from '../../ContractRewardsList/ContractRewardsList';
import { ContractSubtitle } from '../../ContractSubtitle/ContractSubtitle';
import { OpenContractTimer } from '../../ContractTimer/OpenContractTimer';
import WarningTimer from '../../WarningTimer/WarningTimer';
import { GrandContractTerms } from '../GrandContractTerms/GrandContractTerms';

interface GrandContractDetailProps {
  kind: SpecContractType | PaidSpecContractType;
  status: SpecContractStatusType;
}

const GrandContractDetail: FC<GrandContractDetailProps> = ({
  kind,
  status,
}) => {
  const timeToNextContract = useNextSpecContractTime(kind);
  const hours = Math.floor((timeToNextContract || 0) / 3600);
  const isContractExpired = status === 'expired';

  return (
    <div className="w-full flex flex-col items-center relative z-[2]">
      <OpenContractTimer isExtendsTimer={isContractExpired} kind={kind} />
      <GrandContractTerms status={status} kind={kind} />
      {isContractExpired ? (
        <div className="flex flex-col items-center mt-6 mb-8">
          <ContractExpiredText />
          <AdditionalTimeInfo kind={kind} className="mt-5" />
        </div>
      ) : (
        <>
          {hours > 3 ? (
            <>
              <ContractSubtitle kind={kind} />
              <ContractDescription kind={kind} />
            </>
          ) : (
            <WarningTimer
              timer={timeToNextContract || 0}
              classNames="my-[40px]"
            />
          )}
        </>
      )}
      {!isContractExpired && (
        <ContractRewardsList kind={kind} className="mb-8" />
      )}
      <ContractButtons kind={kind} isContractExpired={isContractExpired} />
    </div>
  );
};

export { GrandContractDetail };
